/**
 * English. 모양은 ko.ts를 따른다 — 빠진 문구가 있으면 빌드가 실패한다.
 * 좁은 폰(320px)에서 버튼·칩이 넘치지 않게 짧은 표현을 고른다.
 */

import type { Messages } from './ko';

export const en: Messages = {
  common: {
    back: 'Back',
    menu: 'Menu',
    profile: 'Profile',
    mainNav: 'Main',
    tripDates: 'Trip dates',
    save: 'Save',
    add: 'Add',
    delete: 'Delete',
    cancel: 'Cancel',
    processing: 'Working…',
    failed: 'Something went wrong',
    loading: 'Loading…',
    beta: 'beta',
    tripNotFound: 'Trip not found.',
    itemNotFound: 'Item not found.',
    noInfo: 'No data',
    querying: 'Checking',
    dayN: (n) => `Day ${n}`,
    dayWithDate: (n, date) => `Day ${n} · ${date}`,
  },

  tabs: { home: 'Home', schedule: 'Plan', map: 'Map' },

  kind: {
    place: 'Place',
    flight: 'Flight',
    train: 'Train',
    bus: 'Bus',
    ferry: 'Ferry',
  },

  transport: {
    walk: 'Walk',
    transit: 'Transit',
    car: 'Car',
  },

  carrierLabel: {
    place: '',
    flight: 'Flight no.',
    train: 'Train',
    bus: 'Bus route',
    ferry: 'Route · Ship',
  },

  carrierPlaceholder: {
    place: '',
    flight: 'e.g. KE1201',
    train: 'e.g. Shinkansen Nozomi 21',
    bus: 'e.g. Airport Limousine 6001',
    ferry: 'e.g. Busan → Fukuoka Beetle',
  },

  region: { KR: 'Korea', GLOBAL: 'Abroad' },

  leg: {
    late: (d: string) => `Not enough time · ${d} late`,
    transitMissing: 'No transit data',
    transitNoRoute: 'No line',
    crossBorder: 'International · enter manually',
    crossBorderShort: 'International',
    needsPlace: 'Add a place to calculate',
    unknownTap: 'No route info · tap to enter',
    unknown: 'No route info',
    manual: 'manual',
  },

  signIn: {
    method: {
      google: 'Continue with Google',
      apple: 'Continue with Apple',
      dev: 'Try with a dev account',
    },
    tagline: 'Plan trips together with friends',
    invited: "You've been invited to a trip. Sign in and you'll go straight to it.",
    devNote:
      'No backend is connected yet, so the session stays **in this browser only**. Invites and shared editing work once Supabase is connected.',
    privacy: "Other people only ever see your nickname. The email you sign in with stays hidden.",
    privacyLink: "Privacy policy",
    features: [
      { title: 'Edit together', body: 'Friends’ edits show up right away, no refresh. You can see who changed what, and when.' },
      { title: 'Maps & travel times', body: 'Add places and get walking and transit times to the next plan. Kakao Map in Korea, Google Maps elsewhere.' },
      { title: 'Shared expenses', body: 'Log who paid and it works out who owes whom. Mixed currencies are fine.' },
    ],
    shotAlt: 'A day’s plan, with travel times between plans',
  },

  inApp: {
    opening: "Opening in your browser…",
    openedTitle: "Opened in your browser",
    openedBody: (app: string) => `Google blocks sign-in inside ${app}, so we opened this page in your default browser (Safari, Chrome…). You can close this window. If nothing opened, use the steps below.`,
    backToKakao: "Back to KakaoTalk",
    retry: "Open again",
    manualTitle: (app: string) => `You can’t sign in inside ${app}`,
    manualBody: "Google blocks sign-in in in-app browsers. Please open this page in your default browser, like Safari or Chrome.",
    steps: "Tap the ⋯ menu at the top or bottom and choose “Open in Safari” or “Open in browser”, or copy the link and paste it into your browser.",
    linkLabel: "This page’s address",
    copyLink: "Copy link",
    copied: "Copied — paste it into your browser",
    apps: { kakaotalk: "KakaoTalk", line: "LINE", instagram: "Instagram", facebook: "Facebook", naver: "the NAVER app", band: "BAND", daum: "the Daum app", everytime: "Everytime", webview: "this app" },
  },

  unavailable: {
    title: 'The service is temporarily unavailable',
    body: "We're under maintenance or hit a temporary problem. Please try again in a moment.",
    retry: 'Try again',
  },

  home: {
    title: 'Home',
    upcoming: 'Upcoming',
    pastTrips: (n) => (n === 1 ? '1 past trip' : `${n} past trips`),
    stats: (items, members) =>
      `${items === 1 ? '1 plan' : `${items} plans`} · ${members === 1 ? '1 person' : `${members} people`}`,
    dayCount: (n) => (n === 1 ? '1 day' : `${n} days`),
    bothRegions: 'Korea + abroad',
    dday: (n) => `D-${n}`,
    departsToday: 'Leaving today',
    ongoing: 'On the trip',
    past: 'Past trip',
    newTrip: 'New trip',
    joinByCode: 'Join with invite code',
  },

  guide: {
    title: 'How to use',
    link: 'New here? See how it works',
    intro: 'Plan trips together with friends. Create a trip, invite friends, and every edit shows up on everyone’s screen right away.',
    shotAlt: (title) => `${title} screen`,
    steps: [
      {
        title: 'Create a trip',
        body: 'On Home, tap “New trip” and pick a name, dates and cover. Going abroad? Set the default time zone to that city so plans show in local time. For days in another city, tap the city name on the plan screen.',
      },
      {
        title: 'Invite friends',
        body: 'On the plan screen, open the menu (≡) → “Members · invite code” and send the invite link over a messenger. When a friend opens it and signs in, they join the trip right away. No link? They can enter the code under “Join with invite code” on Home.',
      },
      {
        title: 'Add plans',
        body: 'Pick a day and tap “+ Add to plan”. Search for a place and the results appear as numbered pins on the map, so you can pick the right branch. For flights, trains and buses, change the type and add departure and arrival.',
      },
      {
        title: 'See the day',
        body: 'Walking, transit and driving times between plans are calculated for you. Tap a leg to change how you get there or set the time yourself. Swipe sideways to move to another day.',
      },
      {
        title: 'Map',
        body: 'The “Map” tab shows the day’s route. Kakao Map in Korea, Google Maps elsewhere. “Directions” on a plan opens your map app with the destination set.',
      },
      {
        title: 'Do it together',
        body: 'The menu (≡) has Expenses (log who paid, and it settles up), Ideas (vote on places), Checklist, Change history (who changed what) and Send this day’s plan.',
      },
      {
        title: 'During the trip',
        body: 'While you’re traveling, a Today card sits at the top of Home: what’s on now, time until the next plan, weather and directions to the next place.',
      },
    ],
    tipsTitle: 'Good to know',
    tips: [
      {
        q: 'How do I find this guide again?',
        a: 'Tap ? at the top left of Home, open the menu (≡) on the plan screen → “How to use”, or use “How to use” at the bottom of your profile.',
      },
      {
        q: 'Can I use it like an app?',
        a: 'Add it to your home screen. iPhone: Safari’s Share button → “Add to Home Screen”. Android: Chrome menu (⋮) → “Add to Home screen” (or “Install app”).',
      },
      {
        q: 'A link from a messenger opened in another browser',
        a: 'That’s expected. Google blocks sign-in inside in-app browsers, so we move you to Safari or Chrome.',
      },
      {
        q: 'What if I’m offline?',
        a: 'Trips you’ve opened before still show. Edits made offline are kept on your device and sent once you’re back online.',
      },
      {
        q: 'Does it cost anything?',
        a: 'It’s free, with no ads. Others only see your nickname.',
      },
    ],
    start: 'Get started',
  },

  start: {
    title: 'Plan trips together with friends',
    body: 'Create a trip, invite friends, and fill in the plan, map and expenses together.',
    step1: 'Create a trip — just a name and dates',
    step2: 'Invite friends — by link or code',
    step3: 'Plan together — edits show up instantly',
    noTrip: 'No trips yet',
    scheduleBody: 'Once you create a trip, you can plan each day here.',
    mapBody: 'The map shows the places in your plan, day by day.',
  },

  today: {
    /** 지금 하고 있는 일정 — 오늘 카드와 일정 목록 */
    now: "Now",
    until: (end: string) => `until ${end}`,
    label: 'Today',
    dayCity: (n, city) => `Day ${n} · ${city}`,
    localTime: (time) => `Local ${time}`,
    next: 'Up next',
    inTime: (d) => `in ${d}`,
    allDone: "That's everything for today.",
    empty: 'Nothing planned for today yet.',
    weather: {
      clear: 'Clear',
      cloudy: 'Cloudy',
      fog: 'Fog',
      rain: 'Rain',
      snow: 'Snow',
      storm: 'Storm',
    },
    rainChance: (n) => `${n}% chance of rain today`,
    rate: (from, to) => `1 ${from} ≈ ${to}`,
  },

  trip: {
    title: 'Plan',
    notFound: "Trip not found. It may have been deleted, or you're no longer a member.",
    tzBanner: (delta, city) =>
      `Time difference from yesterday: **${delta}**. All times below are **local time in ${city}**.`,
    emptyDay: 'Nothing planned for this day yet.',
    addItem: 'Add to plan',
    /** 일정 화면 위의 바로가기 — 메뉴 속 자주 쓰는 기능을 상태와 함께 */
    shortcuts: 'Trip shortcuts',
    shortcutChecklist: (done: number, total: number) => total ? `Packing ${done}/${total}` : 'Packing',
    shortcutLedger: (n: number) => n ? `Expenses · ${n}` : 'Expenses',
    shortcutIdeas: (n: number) => n ? `Ideas · ${n}` : 'Ideas',
    menuChecklist: 'Checklist',
    timeConflict:
      "Some times are earlier than the plan before them. Unless it's past midnight, check the order.",
    timeConflictShort: 'Earlier than the plan before',
    sortByTime: 'Sort by time',
    menuReorder: 'Reorder',
    menuOptimize: 'Optimize route',
    reorderDone: 'Done',
    reorderHint: "Drag the handle on the right to reorder. Times you've set won't change.",
    reorderHandle: (title) => `Move ${title}`,
    menuLedger: 'Expenses',
    menuIdeas: 'Ideas',
    menuCalendar: 'Export to calendar',
    menuShareDay: "Send this day's plan",
    menuActivity: 'Change history',
    lodgingFrom: (name: string) => `Leaving from ${name}`,
    lodgingAt: (name: string) => `Staying at ${name}`,
    addLodging: 'Set where you stay tonight',
    newBadge: 'New',
    newBadgeAria: (who: string) => `Recently changed by ${who}`,
    menuShare: 'Share with friends',
    menuMembers: 'Members · invite code',
    menuDelete: 'Delete trip',
    menuLeave: 'Leave trip',
    deleteTitle: 'Delete this trip?',
    deleteConfirm: 'Delete',
    deleteBody: (name) =>
      `All plans and checklist items in **${name}** will be deleted. This can't be undone.`,
    deleteOthers: (n) =>
      n === 1
        ? 'The other member will also lose access to this trip.'
        : `The other ${n} members will also lose access to this trip.`,
    leaveTitle: 'Leave this trip?',
    leaveConfirm: 'Leave',
    leaveBody: (name) =>
      `**${name}** will disappear from your list. The plan stays as is for everyone else.`,
    leaveRejoin: "You'll need a new invite link to come back.",
  },

  optimize: {
    title: 'Optimize route',
    distance: (before, after) => `Travel distance ${before} → ${after}`,
    saving: (pct) => `${pct}% shorter (straight-line)`,
    noGain: 'This order is already the shortest.',
    note: "The first plan, trains/flights and plans without a location stay put. Times you've set won't change.",
    noteLodging:
      'Calculated from and to where you stay. Trains, flights and plans without a location stay put; times you wrote are kept.',
    timesOff: "⚠ Some plans will be out of order with the times you've set. Adjust the times after applying.",
    apply: 'Use this order',
    close: 'Close',
    moved: 'moved',
  },

  dayEdit: {
    label: 'Day settings',
    city: 'City',
    timezone: 'Time zone',
    keepTimes: (city) => `Times stay the same. A 09:00 plan becomes 09:00 local time in ${city}.`,
    withFollowing: (n) =>
      n === 1 ? 'Also change the next day' : `Also change the following ${n} days`,
    until: (date, city) => `Until ${date} · now ${city}`,
    lodging: 'Where you stay',
    lodgingPlaceholder: 'Search hotels or stays',
    lodgingHint: 'Travel time is calculated from the last plan to here, and from here to the next day\'s first plan.',
  },

  tripCreate: {
    title: 'New trip',
    create: 'Create',
    creating: 'Creating…',
    cover: 'Cover',
    coverAria: (emoji) => `Cover ${emoji}`,
    name: 'Trip name',
    namePlaceholder: 'e.g. Tokyo & Osaka, 6 days',
    start: 'Start',
    end: 'End',
    timezone: 'Default time zone',
    timezoneHint:
      'Every day gets this time zone. On days you move to another city, tap the city name on the plan screen to change it from that day.',
    dateCount: (n) => (n === 1 ? '1 day will be created' : `${n} days will be created`),
    submit: 'Create trip',
    failed: "Couldn't create the trip",
  },

  itemEdit: {
    timeSuggested: (title: string) => `Filled in with when the previous plan (${title}) ends. Add travel time as needed.`,
    deleteTitle: "Delete this plan?",
    deleteBody: ["It disappears for everyone on this trip.", "Its comments are deleted too. This can’t be undone."],
    titleNew: 'Add to plan',
    titleEdit: 'Edit plan',
    date: 'Day',
    dateMoveHint: 'Moves to the end of that day. The time stays the same.',
    kind: 'Type',
    title: 'Title',
    titlePlaceholder: 'e.g. Lunch · ramen',
    place: 'Place',
    placePlaceholder: 'Search places (e.g. Shibuya Crossing)',
    depart: 'Departs from',
    departPlaceholder: 'e.g. Seoul Station, Incheon Airport',
    arrive: 'Arrives at',
    arrivePlaceholder: 'e.g. Busan Station, Jeju Port',
    time: 'Time',
    stay: 'Duration',
    minutesSuffix: 'min',
    bookingRef: 'Booking number',
    bookingRefPlaceholder: 'Flight, hotel or tour confirmation',
    memo: 'Notes',
    memoPlaceholder: 'Booking number, things to bring, tips…',
    defaultTitle: 'New plan',
  },

  itemDetail: {
    stay: (d) => `Stay ${d}`,
    howToMove: 'Getting there',
    firstOfDay: 'First plan of the day — nothing before it.',
    transitMissing:
      "No transit route was found. This area may have no transit data, or nothing runs at that time. Only walking and driving were calculated. If you'll use local options like Grab or tuk-tuks, enter the time below.",
    crossBorder:
      "This leg crosses a border and can't be routed. Please enter the flight or train details yourself.",
    moveTime: 'Travel time',
    minus5: '5 minutes less',
    plus5: '5 minutes more',
    saveManual: 'Save this time',
    manualNote: "Entered manually. Route results won't overwrite it.",
    revertAuto: 'Go back to automatic',
    memo: 'Notes',
    noMemo: 'No notes yet.',
    drawnLine: 'Line on the map',
    viaTransit: 'Transit lines found',
    viaCar: 'Driving route along roads and sea routes',
    straight: 'No route found, so the two terminals are joined with a dashed straight line.',
    coordNote: 'Found from coordinates only.',
    carrierDiffers: (code) => ` It may differ from "${code}" you entered.`,
    edit: 'Edit',
    copy: 'Copy',
    copied: 'Copied',
  },

  edited: {
    leftMember: 'Former member',
    title: (name, when) => `Edited by ${name ?? 'you'}${when ? ` · ${when}` : ''}`,
    line: (name) => `Last edited by ${name ?? 'you'}`,
  },

  map: {
    title: 'Map',
    noCoords: 'No plans with a location on this day.',
    noTime: 'No time set',
    startPoint: 'Starting point of the day.',
    loading: 'Loading map…',
    failed: "Couldn't load the map",
    fallback: "Couldn't load the map, so here's a simple one. The order and route are unchanged.",
    schematic: 'Simple map',
    pinAria: (n, time, name) => `Stop ${n}: ${time ? `${time} ` : ''}${name}`,
  },

  place: {
    searchFailed: "Couldn't find places",
    searchHttpFailed: (status) => `Place search failed (${status}). Please try again shortly.`,
    searching: 'Searching…',
    noCoord: "No coordinates, so it won't show on the map.",
    searchMore: 'Search more landmarks by name',
    moreNone: 'Nothing more found. Try English or the local language.',
    pinnedName: 'Pinned location',
  },

  activity: {
    title: 'Change history',
    needsDb: "Change history isn't available yet. Please try again later.",
    offline: 'Available when you reconnect.',
    empty: 'No history yet. Adding or editing plans will show up here.',
    today: 'Today',
    yesterday: 'Yesterday',
    line: (
      who: string | null,
      target: 'item' | 'expense' | 'place',
      action: 'add' | 'update' | 'delete',
      title: string,
      day: number | null,
    ) => {
      const verb = {
        item: { add: 'added', update: 'edited', delete: 'deleted' },
        expense: { add: 'logged the expense', update: 'edited the expense', delete: 'deleted the expense' },
        place: { add: 'suggested', update: 'edited the idea', delete: 'removed the idea' },
      }[target][action];
      return `${who ?? 'You'} ${verb} “${title}”${day ? ` (Day ${day})` : ''}`;
    },
  },

  shareDay: {
    heading: (emoji: string, trip: string, n: number, date: string, city: string) =>
      `${emoji} ${trip} · Day ${n}, ${date} · ${city}`,
    empty: 'No plans yet.',
    copied: "Copied this day's plan — paste it into your chat",
    failed: "Couldn't share. Please try again.",
  },

  directions: {
    open: 'Directions',
    kakao: 'KakaoMap',
    google: 'Google Maps',
    aria: (app: string, place: string) => `Directions to ${place} in ${app}`,
  },

  placeSearch: {
    region: 'Region',
    domestic: 'Korea',
    overseas: 'Abroad',
    domesticPlaceholder: 'e.g. Gyeongbokgung, Seoul Station',
    overseasPlaceholder: 'e.g. Tokyo Skytree, Shinjuku',
    hint: 'Pick from search, or move the map to put the center pin on the spot',
    confirm: 'Use this location',
    nameOnly: 'Just the name, no location',
    mapUnavailable: "Couldn't load the map. Pick a search result or enter just the name.",
    noResultsKr: 'No results. You can move the map and use the center pin instead.',
    noResultsGlobal: 'No results. Try English or the local language, or move the map and use the pin.',
    clear: 'Clear place',
  },

  credits: {
    title: 'Data sources',
    searchUse: 'Place search · routing',
    osm: '© OpenStreetMap contributors',
    krSearchUse: 'Place search in Korea',
    kakao: 'Kakao',
    ratesUse: 'Exchange rates',
    weatherUse: 'Weather',
    transitUse: 'Public transit',
    transitous: 'Transitous (agency GTFS)',
  },

  ledger: {
    title: 'Expenses',
    needsDb: "Expenses aren't available yet. Please try again later.",
    total: 'Total spent',
    myShare: 'My share',
    settleIn: 'Settle in',
    settleTitle: 'Settle up',
    allSettled: 'Everyone is square.',
    byCurrency: "Couldn't load exchange rates, so each currency is settled separately.",
    ratesAsOf: (date) => `Exchange rates as of ${date}.`,
    empty: "No expenses yet. Just note who paid — the app works out who owes what.",
    add: 'Add expense',
    addTitle: 'Add expense',
    editTitle: 'Edit expense',
    what: 'What was it for',
    whatPlaceholder: 'e.g. Dinner · som tam',
    amount: 'Amount',
    currency: 'Currency',
    paidBy: 'Paid by',
    splitAmong: 'Split between',
    splitEach: (n, each) => `${each} each for ${n}`,
    splitNone: 'Pick at least one person',
    date: 'Day',
    noDate: 'No date',
    paidLine: (name, n) => `Paid by ${name} · split ${n} ways`,
    deleteTitle: 'Delete this expense?',
    deleteBody: 'Balances will be recalculated.',
    approx: (amount) => `≈ ${amount}`,
    sends: 'From',
    receives: 'To',
  },

  ideas: {
    title: 'Ideas',
    needsDb: "Ideas aren't available yet. Please try again later.",
    empty: 'Add places you want to go and vote. Put the most-voted ones into the plan.',
    add: 'Add an idea',
    addTitle: 'Add a place idea',
    where: 'Where',
    wherePlaceholder: 'Search places (e.g. Seongsu cafe street)',
    note: 'Note',
    notePlaceholder: 'Why go, price, need to book…',
    addedBy: (name) => `Added by ${name}`,
    vote: 'I want to go',
    voted: 'Voted',
    votes: (n) => (n === 1 ? '1 vote' : `${n} votes`),
    toPlan: 'Add to plan',
    toPlanTitle: 'Which day?',
    toPlanHint: 'It goes to the end of that day and leaves the ideas list.',
    removeTitle: (name) => `Remove "${name}"?`,
    removeBody: 'Its votes will be removed too.',
  },

  comments: {
    title: 'Comments',
    placeholder: 'Add a comment',
    send: 'Send',
    empty: 'No comments yet.',
    delete: 'Delete',
    count: (n) => (n === 1 ? '1 comment' : `${n} comments`),
  },

  checklist: {
    title: 'Checklist',
    empty: 'No items yet.',
    fromTemplate: 'Pick from a starter list',
    templateTitle: 'Packing basics',
    groupCommon: 'Any trip',
    groupAbroad: 'Going abroad',
    already: 'Already added',
    addSome: (n: number) => (n > 0 ? `Add ${n}` : 'Nothing selected'),
    templates: {
      common: ['Phone charger', 'Power bank', 'ID card', 'Cards & cash', 'Basic medicine', 'Toiletries', 'Change of clothes', 'Umbrella'],
      abroad: ['Passport', 'Passport copy', 'Flight e-ticket', 'Hotel booking', 'SIM / eSIM', 'Travel insurance', 'Local currency', 'Card for overseas payments', 'Plug adapter'],
    },
    addPlaceholder: 'Add item',
  },

  members: {
    title: 'Members',
    screenTitle: 'Members · Invite',
    code: 'Invite code',
    codeAria: (spelled) => `Invite code ${spelled}`,
    sendLink: 'Send invite link',
    regenerate: 'Change invite code',
    count: (n) => (n === 1 ? '1 person' : `${n} people`),
    me: ' (you)',
    owner: 'Owner',
    kick: 'Remove',
    alone: "It's just you so far. Send the invite link and friends can join and edit right away.",
    kickTitle: (name) => `Remove ${name}?`,
    kickBody: "They won't be able to see this trip anymore. Their edits stay.",
    kickBody2:
      'If they still have the invite link, they can rejoin. Change the invite code to stop that.',
    regenTitle: 'Change the invite code?',
    regenConfirm: 'Make new code',
    regenBody: (code) => `Links and the code **${code}** sent so far will stop working.`,
    regenBody2: 'Current members stay.',
  },

  invite: {
    title: 'Join a trip',
    joining: 'Joining…',
    code: 'Invite code',
    placeholder: 'e.g. 5G5D5UNX',
    hint: 'Not case-sensitive. If you open a link from a friend, this screen joins for you.',
    join: 'Join',
    goHome: 'Home',
    failed: "Couldn't join",
  },

  share: {
    text: (name, code) => `Let's plan ${name} together (invite code ${code})`,
    textNoUrl: (name, code) =>
      `Let's plan ${name} together\nInvite code: ${code}\nIn the app, tap "Join with invite code" and enter it.`,
    copiedLink: 'Invite link copied',
    copiedCode: 'Invite code copied',
    codeToast: (code) => `Invite code: ${code}`,
  },

  profile: {
    contact: 'Questions & bug reports',
    contactSubject: '[TravelTomodachi] Feedback',
    signOutPendingTitle: (n: number) => `${n} change${n === 1 ? '' : 's'} not sent yet`,
    signOutPendingBody: "If you sign out now, changes you made offline won't reach the server and will be lost. Connect to the internet first, then sign out.",
    title: 'Profile',
    notSignedIn: "You're not signed in.",
    via: {
      google: 'Signed in with Google',
      apple: 'Signed in with Apple',
      dev: 'Signed in with a dev account',
    },
    nickname: 'Nickname',
    nicknamePlaceholder: 'Name your friends will see',
    nicknameHint: (tag) =>
      `It's fine if someone else uses it${tag ? ` — the number (#${tag}) tells you apart` : ''}. You can change it anytime.`,
    saved: 'Saved',
    saveNickname: 'Save nickname',
    signOut: 'Sign out',
    privacy: "Other people only ever see your nickname. The email you sign in with stays hidden.",
    privacyLink: "Privacy policy",
    defaultNickname: 'Traveler',
    language: 'Language',
    languageAuto: 'Use device language',
    languageAutoHint: (name) => `Device language: ${name}`,
    languageManualHint: 'Only this device uses the language you pick.',
    deleteAccount: "Delete account",
    deleteTitle: "Delete your account?",
    deleteConfirm: "Delete account",
    /** 탈퇴하면 무엇이 어떻게 되는지 — 확인 창에 한 줄씩 */
    deleteBody: ["Your nickname and sign-in details (such as your email) are deleted right away. This can't be undone.", "Trips you created go to the member who joined them first. Trips where you're the only member are deleted.", "Plans, comments and expenses you added to shared trips stay, shown as by a “former member”."],
    deleteFailed: "Couldn't delete your account",
  },

  privacyPage: {
    title: "Privacy policy",
    effective: "Effective",
    /** 번역본이면 공식 문서가 한국어라는 안내. 한국어는 null */
    authoritative: "This is an English translation. The Korean version is the official policy.",
  },

  nicknameSetup: {
    title: 'What should we call you?',
    sub: 'Friends on the same trip will see this name.',
    placeholder: 'e.g. Sky',
    hint: "It's fine if someone else uses it. You can change it anytime in your profile.",
    invited: "Once you pick a nickname, you'll go straight to the trip you were invited to.",
    start: 'Get started',
    later: 'Decide later',
  },

  offline: {
    offline: 'Offline — showing the plan you last loaded. Your changes will be sent when you reconnect.',
    serverDown: "Couldn't reach the server, so this is the plan you last loaded.",
    pending: (n: number) => `${n} change${n === 1 ? '' : 's'} not sent yet — will send when you reconnect`,
    syncing: 'Sending your changes…',
    dropped: (n: number, reason: string) =>
      `${n} offline change${n === 1 ? '' : 's'} couldn't be saved${reason ? ` (${reason})` : ''}. Please check the plan.`,
  },

  nickname: {
    empty: 'Please enter a nickname',
    tooLong: (max) => `Nicknames can be up to ${max} characters`,
    hash: "Nicknames can't contain '#'",
  },

  errors: {
    signInFailed: "Couldn't sign in",
    signInCancelled: 'Sign-in was cancelled',
    signInNoCode: 'The sign-in response had no code',
    unsupportedMethod: 'This sign-in method is not supported',
    notSignedIn: "You're not signed in",
    needSignIn: 'Please sign in',
    nicknameFailed: "Couldn't change the nickname",
    readProfile: "Couldn't load the profile",
    createProfile: "Couldn't create the profile",
    saveFailed: "Couldn't save",
    readTrips: "Couldn't load trips",
    readMembers: "Couldn't load members",
    readDays: "Couldn't load days",
    readItems: "Couldn't load plans",
    createTrip: "Couldn't create the trip",
    createDays: "Couldn't create the days",
    removeMember: "Couldn't remove the member",
    removeMemberNone: 'No permission, or the member already left',
    deleteTrip: "Couldn't delete the trip",
    deleteTripNotOwner: 'Only the trip owner can delete it',
    createItem: "Couldn't add the plan",
    updateItem: "Couldn't update the plan",
    deleteItem: "Couldn't delete the plan",
    updateDays: "Couldn't update the days",
    addChecklist: "Couldn't add the item",
    updateChecklist: "Couldn't update the item",
    deleteChecklist: "Couldn't delete the item",
    addExpense: "Couldn't add the expense",
    updateExpense: "Couldn't update the expense",
    deleteExpense: "Couldn't delete the expense",
    addPlace: "Couldn't add the idea",
    removePlace: "Couldn't remove the idea",
    vote: "Couldn't vote",
    addComment: "Couldn't post the comment",
    deleteComment: "Couldn't delete the comment",
  },

  serverErrors: {
    needSignIn: 'Please sign in',
    codeNotFound: "That invite code doesn't exist",
    onlyOwnerRegenerate: 'Only the trip owner can change the invite code',
    regenerateFailed: "Couldn't make a new invite code. Please try again",
    nicknameCrowded: 'Too many people use this nickname. Please pick another one',
    tooMany: 'This trip has reached the maximum number of entries',
    tooManyTrips: 'You’ve reached the maximum number of trips',
  },
};
